import { FC, useRef, useState } from 'react';
import FieldTitle from '../FieldTitle/FieldTitle';
import styles from '../InsuranceBookingConfirmation.module.scss';
import { ReactComponent as DropdownIcon } from 'assets/icons/keyboard_arrow_down.svg';
import { useAppDispatch, useAppSelector } from 'redux/reduxHooks';
import { useForm } from 'react-hook-form';
import FormTextInput from 'components/BookingForm/FormTextInput/FormTextInput';
import { ClickAwayListener, Popper } from '@material-ui/core';
import moment from 'moment';
import NavigationBtnControl from 'components/BookingForm/NavigationBtnControl/NavigationBtnControl';
import { updateBookingInsurance } from 'services/APIs';
import { loadOtpToken } from 'utils/storage';
import { resetTempAccessToken } from 'redux/authSlice';
import { useHistory } from 'react-router-dom';
import useNotifyBookingCompleted from 'hooks/useNotifyBookingCompleted';
import LoadingScreen from 'components/LoadingScreen/LoadingScreen';

interface InsuranceBookingConfirmationFormValues {
  memberId: string;
  groupNumberId: string;
  subscriberDob: string;
  subscriberFullName: string;
  employerName: string;
  relationToSubscriber: string;
}

interface InsuranceInputFormProps {}

const INSURANCE_RELATIONS = ['Self', 'Spouse', 'Parents/ Guardian', 'Other'];

const InsuranceInputForm: FC<InsuranceInputFormProps> = () => {
  const {
    control,
    setValue,
    watch,
    formState: { isValid, isSubmitting },
    handleSubmit,
  } = useForm<InsuranceBookingConfirmationFormValues>({
    mode: 'all',
    defaultValues: {
      memberId: '',
      groupNumberId: '',
      subscriberDob: '',
      subscriberFullName: '',
      employerName: '',
      relationToSubscriber: '',
    },
  });

  const {
    bookingFormSlice: {
      step1: { phoneNumber },
      step3: { patientInfo, bookingId, tempAccessToken },
    },
    clinicDetailsSlice: {
      clinicData: { isInsuranceRequired },
    },
  } = useAppSelector((state) => state);

  const dispatch = useAppDispatch();

  const buttonElementRef = useRef<HTMLButtonElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const history = useHistory();

  const { onBookingCompleted, isRedirectEnabled } = useNotifyBookingCompleted();

  const [isRedirecting, setIsRedirecting] = useState(false);

  const { relationToSubscriber } = watch();

  const renderPlaceHolderText = (placeholder: string) => {
    let currentText = placeholder;
    return isInsuranceRequired ? currentText : `${currentText} (optional)`;
  };

  const populateCommonProps = () => {
    return {
      type: 'text',
      control,
      ...(isInsuranceRequired && {
        rules: { required: 'This field is required' },
      }),
    };
  };

  const onSelectPatientRelationToSubscriber = (relation: string) => {
    const self = INSURANCE_RELATIONS[0];
    if (relation === self) {
      setValue(
        'subscriberDob',
        moment(patientInfo!.dob, 'YYYY-MM-DD').format('MM/DD/YYYY')
      );
      setValue(
        'subscriberFullName',
        `${patientInfo!.firstName} ${patientInfo!.lastName}`
      );
    } else {
      setValue('subscriberDob', '');
      setValue('subscriberFullName', '');
    }
    setIsOpen(false);
    setValue('relationToSubscriber', relation);
  };

  const onSubmit = async (data: InsuranceBookingConfirmationFormValues) => {
    const payload = {
      ...data,
      subscriberDob: data.subscriberDob
        ? moment(data.subscriberDob).format('YYYY-MM-DD')
        : '',
      bookingId,
    };

    try {
      await updateBookingInsurance(payload, {
        otpToken: loadOtpToken(phoneNumber),
        tmpAccessToken: tempAccessToken,
      });

      dispatch(resetTempAccessToken());

      if (isRedirectEnabled) {
        setIsRedirecting(true);
        onBookingCompleted();
      }

      return history.go(0);
    } catch (error) {}
  };

  return (
    <>
      {isRedirecting && <LoadingScreen message="Redirecting..." />}
      <div className={styles['group-container']}>
        <div>
          <FieldTitle>Group ID</FieldTitle>
          <FormTextInput
            name="groupNumberId"
            placeholder={renderPlaceHolderText('Input group ID')}
            {...populateCommonProps()}
          />
        </div>

        <div>
          <FieldTitle>Member ID</FieldTitle>
          <FormTextInput
            name="memberId"
            placeholder={renderPlaceHolderText('Input member ID')}
            {...populateCommonProps()}
          />
        </div>
      </div>

      <ClickAwayListener onClickAway={() => setIsOpen(false)}>
        <div className={styles['dropdown-container']}>
          <FieldTitle>Patient’s Relation to Subscriber</FieldTitle>
          <button
            ref={buttonElementRef}
            className={`${styles['button']} ${
              relationToSubscriber ? styles['button-selected'] : ''
            }`}
            onClick={() => setIsOpen(!isOpen)}
          >
            <span>
              {relationToSubscriber
                ? relationToSubscriber
                : renderPlaceHolderText('Select')}
            </span>
            <DropdownIcon />
          </button>
          <Popper
            open={isOpen}
            anchorEl={buttonElementRef.current}
            placement="bottom-start"
            style={{ zIndex: 9999 }}
          >
            <div
              className={styles['dropdown']}
              style={{
                width: isOpen ? buttonElementRef.current?.clientWidth : '100%',
              }}
            >
              {INSURANCE_RELATIONS.map((relation) => (
                <button
                  key={relation}
                  onClick={() => onSelectPatientRelationToSubscriber(relation)}
                  data-active={relationToSubscriber === relation}
                >
                  {relation}
                </button>
              ))}
            </div>
          </Popper>
        </div>
      </ClickAwayListener>

      <div className={styles['group-container']}>
        <div>
          <FieldTitle>Subscriber’s Full Name</FieldTitle>
          <FormTextInput
            name="subscriberFullName"
            placeholder={renderPlaceHolderText('Input full name')}
            {...populateCommonProps()}
          />
        </div>

        <div>
          <FieldTitle>Subscriber’s Date of Birth</FieldTitle>
          <FormTextInput
            type="date"
            name="subscriberDob"
            label="Date of Birth"
            control={control}
            placeholder={renderPlaceHolderText('MM/DD/YYYY')}
            mask="99/99/9999"
            rules={{
              validate: (value: string) => {
                if (!value) return true;
                const date = moment(value, 'MM/DD/YYYY', true);
                if (!date.isValid()) {
                  return 'Invalid date of birth format';
                }
              },
            }}
          />
        </div>
      </div>

      <FieldTitle>Employer Name</FieldTitle>
      <FormTextInput
        name="employerName"
        placeholder={renderPlaceHolderText('Input employer name')}
        {...populateCommonProps()}
      />

      <NavigationBtnControl
        nextBtnTitle="Done"
        onNext={handleSubmit(onSubmit)}
        isDisabledNextBtn={!isValid}
        isLoading={isSubmitting}
      />
    </>
  );
};

export default InsuranceInputForm;
